/*  MUI  */
import { Paper, TableCell, Typography } from '@mui/material';

/*  Shared  */
import {
    ApplicationBubbles,
    Sources,
    UserBubbles
} from 'application/Shared/Components/Avatars';
import { DateFormat } from 'application/Shared/Components/Core';
import { ListUserAccounts } from 'application/Shared/Components/ListUserAccounts';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Helpers } from 'application/Shared/Helpers';

/* Hooks */
import { useFetchApplicationAccounts } from 'hooks/dataAccess/User/userApplicationAccounts';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const RenderRow = ({ row }) => {
    const {
        applications,
        firstUsed,
        lastUsed,
        personal,
        service,
        shared,
        totalLogins,
        username,
        users,
        work
    } = row;
    const sources = Helpers.getSourcesLoginForAccounts(row.sources);

    const accounts = [{ personal, service, shared, username, work }];

    return (
        <>
            <TableCell>
                <ListUserAccounts accounts={accounts} />
            </TableCell>
            <TableCell>
                <UserBubbles align="left" max={3} users={users} dataIsUser />
            </TableCell>
            <TableCell>
                <Sources sources={sources} max={3} />
            </TableCell>
            <TableCell>{totalLogins}</TableCell>
            <TableCell>
                <DateFormat date={firstUsed} noTimeString noTz />
            </TableCell>
            <TableCell>
                <DateFormat date={lastUsed} noTimeString noTz />
            </TableCell>
            <TableCell>
                <ApplicationBubbles
                    max={3}
                    applications={applications}
                    tooltipPlacement="left"
                />
            </TableCell>
        </>
    );
};

const headers = (t) => [
    {
        align: 'left',
        field: 'username',
        renderHeader: <strong>{t('Common.account')}</strong>,
        sortable: true,
        sortableFieldType: 'string',
        sortableKey: 'username',
        width: '20%'
    },
    {
        align: 'left',
        field: 'user.id',
        renderHeader: <strong>{t('Common.users')}</strong>,
        sortable: false,
        width: '10%'
    },
    {
        align: 'left',
        field: 'source',
        renderHeader: <strong>{t('Common.source')}</strong>,
        sortable: false,
        width: '15%'
    },
    {
        align: 'left',
        field: 'totalLogins',
        renderHeader: (
            <strong>{t('UsersAccountsAndDevices.totalLogins')}</strong>
        ),
        sortable: true,
        sortableFieldType: 'number',
        sortableKey: 'totalLogins',
        width: '10%'
    },
    {
        align: 'left',
        field: 'firstUsed',
        renderHeader: (
            <strong>{t('UsersAccountsAndDevices.discoveredDate')}</strong>
        ),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'firstUsed',
        width: '15%'
    },
    {
        align: 'left',
        field: 'lastUsed',
        renderHeader: (
            <strong>{t('UsersAccountsAndDevices.lastTimeLogin')}</strong>
        ),
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'lastUsed',
        width: '15%'
    },
    {
        align: 'right',
        field: 'applications',
        renderHeader: <strong>{t('Common.applications')}</strong>,
        sortable: true,
        sortableFieldType: 'date',
        sortableKey: 'lastUsed',
        width: '15%'
    }
];

const csvFields = (t) => [
    { key: 'username', label: t('Common.account') },
    {
        key: 'personal',
        label: t('Reports.AccountsInventory.personal')
    },
    {
        key: 'work',
        label: t('Reports.AccountsInventory.work')
    },
    {
        key: 'shared',
        label: t('Reports.AccountsInventory.shared')
    },
    {
        key: 'service',
        label: t('Reports.AccountsInventory.service')
    },
    {
        key: 'users.length',
        label: t('UsersAccountsAndDevices.numberOfUsers')
    },
    {
        key: 'usersList',
        label: t('Common.users'),
        rowKeyAndDataPath: 'users:name',
        transform: 'listExtract'
    },
    { key: 'sources.LOGIN', label: t('Common.cloud_logins') },
    { key: 'sources.GOOGLE_SSO', label: t('Common.google_sso') },
    { key: 'sources.MICROSOFT_SSO', label: t('Common.microsoft_sso') },
    {
        key: 'sources.AZURE_SIGN_IN',
        label: t('Common.microsoftEntraID.logins')
    },
    { key: 'sources.GOOGLE_SIGN_IN', label: t('Common.google_logins') },
    {
        key: 'totalLogins',
        label: t('UsersAccountsAndDevices.totalLogins')
    },
    {
        key: 'firstUsed',
        label: t('UsersAccountsAndDevices.discoveredDate')
    },
    { key: 'lastUsed', label: t('UsersAccountsAndDevices.lastTimeLogin') },
    {
        key: 'applications.length',
        label: t('UsersAccountsAndDevices.numberOfApps')
    },
    {
        key: 'applicationsList',
        label: t('Common.appsAccessed'),
        rowKeyAndDataPath: 'applications:name',
        transform: 'listExtract'
    }
];

const searchFields = ['username'];

export const UserAccountTable = () => {
    const { t } = useTranslation();
    const { clientId, userId } = useParams();

    const params = useMemo(
        () => ({
            platformUserId: userId,
            size: -1,
            uniqueAccountsOnly: true
        }),
        [userId]
    );

    const { accounts } = useFetchApplicationAccounts({
        clientId,
        params
    });

    const tableCsvFields = useMemo(() => csvFields(t), [t]);

    const tableHeaders = useMemo(() => headers(t), [t]);

    const tableParams = useMemo(
        () => ({
            compactUsernames: true,
            includeTotalLogins: true,
            platformTenantId: clientId,
            size: -1,
            username: accounts
        }),
        [accounts, clientId]
    );

    // If there are accounts, then proceed with the table's rowsApi call
    const rowsApi = useMemo(
        () => (accounts.length ? '/applicationAccounts' : null),
        [accounts]
    );

    // If there are no accounts, then send an empty array as the rows, since there won't be a rowsApi call.
    const rows = useMemo(() => (accounts.length ? null : []), [accounts]);

    return (
        <Paper elevation={0} variant="outlined" data-testid="userAccountTable">
            <Typography variant="h5">{t('Common.accounts')}</Typography>
            <ManagedTable
                csvExport
                csvExportFields={tableCsvFields}
                csvExportFileName="user_accounts"
                emptyRowsMessage={t('Common.accountsNotFound')}
                headers={tableHeaders}
                parameters={tableParams}
                renderBody={(row) => <RenderRow row={row} />}
                renderBodyKey="id"
                renderFooter
                rows={rows}
                rowsApi={rowsApi}
                rowsApiPreload
                rowsPerPage={10}
                searchEnabled
                searchFields={searchFields}
                sortDirection="ASC"
                sortFieldType="string"
                sortKey="username"
            />
        </Paper>
    );
};
